import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { ToastProvider } from '../components'
import { shouldRetry } from '../lib/apiClient'
import { PlatformAccessProvider } from '../lib/platformAccess'

import { ErrorBoundary } from './ErrorBoundary'
import { SessionProvider } from './session'

/**
 * Everything above the router that both consoles share. `SessionProvider` sits
 * inside the router because it reads the URL — `/admin` is the trader's own
 * console and asks a different endpoint than the platform one — while the query
 * client sits outside it, because a route change must not throw the cache away.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            /**
             * A refused request is refused deterministically. Retrying a 403
             * three times produces three audit-log rows and the same answer, so
             * only a transport failure and a 5xx are worth a second attempt.
             */
            retry: shouldRetry,
          },
          // The same rule, and it matters more here: a retried POST is a second
          // write, not a second read.
          mutations: { retry: false },
        },
      }),
  )

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <ToastProvider>
          <SessionProvider>
            <PlatformAccessProvider>{children}</PlatformAccessProvider>
          </SessionProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
